import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildFormAnswerPayload,
  EntityRefUtil,
  formatManuallyWithUserTimezone,
  FormDTO,
  FormElementKind,
  GetChecksResponseItemDTO,
  isSubmissionEditable,
  TableFormElement,
  type UpdateScheduleFormRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { RevisionHistory } from '@/components/revisions/revision-history';
import { SingleRecordPdfControls } from '@/components/single-record-pdf-controls';
import { Sheet, SheetContent, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { useNativeShare } from '@/hooks/use-native-share';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { cn, initials } from '@/lib/utils';
import { checkKeys } from '@/queries/checks';
import { activityKeys } from '@/queries/inspections';
import { getScheduleFormFormQueryOptions, getScheduleFormQueryOptions, getScheduleFormRevisionsQueryOptions, scheduleFormKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { LegacySubmissionRenderer } from '@/utils/legacy';
import { getWorkspaceRoute } from '@/utils/routes';
import { formatScheduleCheckLabel } from '@/utils/schedules';

import { SaveChangesDialog } from '../common/save-changes-dialog';
import { initializeTableInitialValues } from '../form/elements/table';
import { FormElementsRenderer, FormRendererData } from '../form/form-elements-renderer';
import ActivityIcon from '../icons/activity';
import ArrowLeftIcon from '../icons/arrow-left';
import CloseIcon from '../icons/close';
import { ShareIcon } from '../icons/share';
import WarningIcon from '../icons/warning';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';

export default function ViewScheduleRecordDrawer({
  open: openProp,
  onOpenChange,
  scheduleFormId,
  noTrigger = false,
  autoTriggerSharePdf = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  scheduleFormId?: string;
  noTrigger?: boolean;
  autoTriggerSharePdf?: boolean;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const [open, setOpen] = useState(openProp ?? false);
  const { isAWA } = useIsAWA();
  const { shareFile } = useNativeShare();
  const [view, setView] = useState<'form' | 'activity'>('form');

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    setOpen(open);
    if (!open) {
      setView('form');
    }
  };

  const { data: scheduleFormData, isFetching: isFetchingScheduleFormData } = useQuery({
    ...getScheduleFormQueryOptions({ scheduleFormId, isAWA }),
    enabled: !!scheduleFormId && open,
  });

  const { data: scheduleFormForm, isFetching: isFetchingScheduleFormFormData } = useQuery({
    ...getScheduleFormFormQueryOptions({ formRevisionId: scheduleFormData?.formRevisionId, isAWA }),
    enabled: !!scheduleFormId && !!scheduleFormData?.formRevisionId,
  });

  const { data: revisions, isLoading: isLoadingRevisions } = useQuery({
    ...getScheduleFormRevisionsQueryOptions({ scheduleFormId, isAWA }),
    enabled: !!scheduleFormId && open && view === 'activity',
  });

  const [printContentReady, setPrintContentReady] = useState(false);
  const serviceRef = useRef<HTMLDivElement | null>(null);

  const setServiceRef = useCallback((node: HTMLDivElement | null) => {
    serviceRef.current = node;
    if (node) {
      setPrintContentReady(true);
    }
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: serviceRef,
    documentTitle: `${scheduleFormData?.asset ? EntityRefUtil.createAssetLabel(scheduleFormData?.asset) : 'Service'} Details`,
    pageStyle: `
      @page { margin: 30px }
      @media print {
        body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
    `,
  });

  const formInstance = useForm<FormRendererData>({
    defaultValues: {
      ...scheduleFormForm?.elements.reduce(
        (acc, element) => ({
          ...acc,
          ...(element.kind === FormElementKind.Table
            ? {
                [element.id]: [[...initializeTableInitialValues(element as TableFormElement)]],
              }
            : {
                [element.id]: undefined,
              }),
        }),
        {} as FormRendererData,
      ),
    },
  });

  const isLegacySubmission = useMemo(() => scheduleFormData?.legacyData !== null, [scheduleFormData?.legacyData]);

  const hasAutoTriggeredRef = useRef(false);

  // Auto-trigger share PDF when opening from check drawer's "Share as PDF" button
  // Uses printContentReady state (set by callback ref) to ensure DOM is ready before printing
  useEffect(() => {
    if (autoTriggerSharePdf && open && scheduleFormData && !isFetchingScheduleFormData && printContentReady && !hasAutoTriggeredRef.current) {
      hasAutoTriggeredRef.current = true;
      if (scheduleFormData?.document?.url) {
        const url = scheduleFormData?.document.url;
        shareFile({ url, filename: 'schedule-record.pdf' }).then((shared) => {
          if (!shared) {
            window.open(url, '_blank');
          }
        });
      } else {
        handlePrint();
      }
    }
  }, [autoTriggerSharePdf, open, scheduleFormData, isFetchingScheduleFormData, printContentReady, handlePrint, shareFile]);

  // Reset flags when drawer closes
  useEffect(() => {
    if (!open) {
      hasAutoTriggeredRef.current = false;
      setPrintContentReady(false);
    }
  }, [open]);

  useEffect(() => {
    if (scheduleFormData?.data) {
      formInstance.reset(
        scheduleFormData?.data
          .filter((element): element is NonNullable<typeof element> => element !== null)
          .reduce(
            // Type assertion until we migrate to V2 endpoint
            (acc, element) => ({ ...acc, [(element as unknown as { elementId: string }).elementId]: element.value }),
            {} as FormRendererData,
          ),
      );
    }
  }, [scheduleFormData?.data, formInstance]);

  const toast = useToast();
  const queryClient = useQueryClient();

  // Open editing: any logged-in user can edit an eligible record — no owner/role gate.
  const canEditRecord = !!scheduleFormData && !!scheduleFormForm && isSubmissionEditable(scheduleFormData);
  const isFormDirty = formInstance.formState.isDirty;

  const {
    mutate: saveScheduleFormEdit,
    mutateAsync: saveScheduleFormEditAsync,
    isPending: isSaving,
  } = useMutation({
    mutationFn: async (values: FormRendererData) => {
      if (!scheduleFormId) {
        throw new Error('Missing schedule form id');
      }
      const data = buildFormAnswerPayload(scheduleFormForm?.elements ?? [], values);
      const payload: UpdateScheduleFormRequestDTO = { data };
      return await (
        await (isAWA ? AWAApiV2Service.UpdateScheduleForm(scheduleFormId, payload) : ApiService.UpdateScheduleForm(scheduleFormId, payload))
      ).json();
    },
    onError: () => {
      toast.error('Unable to save changes. Please try again.');
    },
    onSuccess: () => {
      toast.success('Schedule record updated');
      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
      queryClient.invalidateQueries({ queryKey: checkKeys.all });
      if (scheduleFormData?.asset?.id) {
        queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(scheduleFormData.asset.id) });
      }
    },
  });

  const handleInvalidSave = () => {
    toast.error('The record has invalid or incomplete data. Please check.');
    const [firstErrorField] = Object.keys(formInstance.formState.errors);
    if (firstErrorField) {
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => formInstance.formState.isDirty);

  async function closeDrawer() {
    if (!(await confirmSafeToLeave())) return;
    handleOpenChange(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) {
          handleOpenChange(true);
        } else {
          closeDrawer();
        }
      }}>
      {!noTrigger && (
        <SheetTrigger asChild>
          <Button className="h-full rounded-none p-2" variant="ghost">
            View Schedule Form Record
          </Button>
        </SheetTrigger>
      )}
      <SheetPortal>
        <SheetContent hideCloseIcon side={'right'} className={cn(isAWA ? 'mx-auto h-full w-full sm:max-w-[800px]' : '')}>
          <SheetHeader className="border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white">
            {isAWA ? (
              <>
                <div className="flex items-center gap-2 py-1">
                  <button className="-m-2 p-2" type="button" onClick={() => closeDrawer()}>
                    <ArrowLeftIcon className="size-6" />
                  </button>
                  <SheetTitle className="text-xl">View schedule check record</SheetTitle>
                </div>
                {canEditRecord && (
                  <Button
                    variant="ghost"
                    className="text-default-green text-lg font-medium"
                    loading={isSaving}
                    disabled={!isFormDirty || isSaving}
                    onClick={() => formInstance.handleSubmit((values) => saveScheduleFormEdit(values), handleInvalidSave)()}>
                    Save
                  </Button>
                )}
              </>
            ) : (
              <>
                <SheetTitle>
                  {isFetchingScheduleFormData || isFetchingScheduleFormFormData
                    ? 'Loading schedule details...'
                    : scheduleFormData?.asset
                      ? EntityRefUtil.createAssetLabel(scheduleFormData?.asset)
                      : 'Schedule'}
                </SheetTitle>
                <div className="flex items-center gap-2">
                  {canEditRecord && (
                    <Button
                      variant="success"
                      loading={isSaving}
                      disabled={!isFormDirty || isSaving}
                      onClick={() => formInstance.handleSubmit((values) => saveScheduleFormEdit(values), handleInvalidSave)()}>
                      Save
                    </Button>
                  )}
                  {scheduleFormData && !isLegacySubmission && (
                    <Button
                      variant="grey"
                      size="default"
                      type="button"
                      icon={<ActivityIcon />}
                      onClick={() => setView((prev) => (prev === 'activity' ? 'form' : 'activity'))}>
                      {view === 'activity' ? 'Hide Activity' : 'Show Activity'}
                    </Button>
                  )}
                  {scheduleFormData && (
                    <SingleRecordPdfControls recordType="schedule-form" recordId={scheduleFormData.id} documentUrl={scheduleFormData.document?.url} />
                  )}
                  <button
                    type="button"
                    className="rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                    onClick={() => closeDrawer()}>
                    <CloseIcon className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </div>
              </>
            )}
          </SheetHeader>

          {(isFetchingScheduleFormData && !scheduleFormData) || (isFetchingScheduleFormFormData && !scheduleFormForm) ? (
            <div className="flex flex-col gap-2 px-6 pt-4 pb-6">
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col gap-1">
                  {[...Array(2)].map((_, i) => (
                    <Skeleton key={i} className={cn('h-4 w-32', i === 1 && 'w-28')} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-end gap-1">
                    {[...Array(2)].map((_, i) => (
                      <Skeleton key={i} className={cn('h-4 w-28', i === 1 && 'w-32')} />
                    ))}
                  </div>
                  <Skeleton className="size-7 rounded-full" />
                </div>
              </div>

              <div className="border-grey-2 animate-pulse border-b-4"></div>

              <div className="flex flex-col gap-4 px-2 py-3">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className={cn('h-12 w-full rounded px-6', i === 6 && 'w-52 self-center')} />
                ))}
              </div>
            </div>
          ) : (
            <div ref={setServiceRef} className="flex h-full flex-1 flex-col overflow-y-auto">
              {view === 'form' && !!scheduleFormData && (
                <div className="flex flex-col gap-1 px-4 pt-4">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex flex-col gap-[2px]">
                      <div>
                        <span className="font-medium">ID: </span> {EntityRefUtil.createScheduleFormSubmissionRef(scheduleFormData?.number)}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Issues: </span>
                        <div className="bg-default-light-orange flex items-center gap-1 rounded px-1.5 py-0.5 text-sm text-white">
                          <WarningIcon className="text-default-dark-orange" size={20} />
                          <span className="text-default-dark-orange">{scheduleFormData?.issueCount || 0}</span>
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const createdByUser = scheduleFormData?.servicedBy ?? scheduleFormData?.submittedBy;
                      const fullName = EntityRefUtil.createUserFullNameRef(createdByUser, '-');
                      const avatarUrl = createdByUser && 'avatarUrl' in createdByUser ? createdByUser.avatarUrl : undefined;
                      return (
                        <div className="flex items-center gap-2 text-right">
                          <div className="flex flex-col gap-[2px]">
                            <div className="line-clamp-1 font-medium">{fullName}</div>
                            <div className="text-secondary-text">
                              {isAWA
                                ? dayjs(scheduleFormData.serviceDate).format('MMM DD, YYYY hh:mm a')
                                : formatManuallyWithUserTimezone(new Date(scheduleFormData.serviceDate), 'MMM DD, YYYY hh:mm a', userTimezone)}
                            </div>
                          </div>
                          <Avatar>{avatarUrl ? <AvatarImage src={avatarUrl} /> : <AvatarFallback>{initials(fullName)}</AvatarFallback>}</Avatar>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="border-default-blue border-b-4" />
                </div>
              )}

              <div
                className={cn(
                  'scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 flex flex-1 flex-col gap-2 overflow-y-auto',
                  view === 'form' && 'px-6 pt-4 pb-6 print:px-0 print:pt-4 print:pb-4',
                )}>
                {view === 'activity' ? (
                  scheduleFormForm && scheduleFormData?.asset ? (
                    <RevisionHistory
                      revisions={revisions ?? []}
                      isLoading={isLoadingRevisions}
                      elements={scheduleFormForm.elements}
                      form={scheduleFormForm.form as FormDTO}
                      asset={scheduleFormData.asset}
                    />
                  ) : (
                    <div className="text-grey-5 p-4 text-sm">Loading…</div>
                  )
                ) : (
                  scheduleFormData && (
                    <>
                      {!!scheduleFormData?.asset && (
                        <div className="flex flex-col gap-1">
                          <Label>Asset</Label>
                          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                            {isAWA ? (
                              <span className="text-sm">{EntityRefUtil.createAssetLabel(scheduleFormData?.asset)}</span>
                            ) : (
                              <Link className="text-default-blue text-sm underline" to={getWorkspaceRoute(`/assets/${scheduleFormData?.asset.id}`)}>
                                {EntityRefUtil.createAssetLabel(scheduleFormData?.asset)}
                              </Link>
                            )}
                          </div>
                        </div>
                      )}

                      {scheduleFormData?.scheduleCheck && (
                        <div className="flex flex-col gap-1">
                          <Label>Schedule Check</Label>
                          <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                            <span className="text-sm">
                              {formatScheduleCheckLabel({
                                scheduleCheck: { label: scheduleFormData?.scheduleCheck.label },
                                schedule: { triggers: scheduleFormData?.scheduleCheck.triggers ?? [] },
                                occurrenceNumber: scheduleFormData?.scheduleCheck.occurrenceNumber ?? 1,
                              } as GetChecksResponseItemDTO) ?? scheduleFormData?.scheduleCheck.label}
                            </span>
                          </div>
                        </div>
                      )}

                      <FormProvider {...formInstance}>
                        <form className="flex flex-col gap-2 print:pb-16">
                          {isLegacySubmission ? (
                            <LegacySubmissionRenderer data={scheduleFormData?.legacyData} createdAt={scheduleFormData?.createdAt} />
                          ) : scheduleFormForm && scheduleFormData?.asset ? (
                            <FormElementsRenderer
                              elements={scheduleFormForm.elements}
                              form={scheduleFormForm.form as FormDTO}
                              asset={scheduleFormData.asset}
                              readonly={!canEditRecord}
                              lockNonEditableElements={canEditRecord}
                              isEditingExistingRecord
                              lockNonLatestScheduleFormEdit={scheduleFormData?.isLatestCheckSubmission === false}
                            />
                          ) : undefined}
                        </form>
                      </FormProvider>
                    </>
                  )
                )}
              </div>
            </div>
          )}

          {/* AWA FAB for Share as PDF */}
          {isAWA && scheduleFormData && (
            <div className="absolute right-4 bottom-4 z-50">
              <Button
                variant="default"
                size="icon"
                className="bg-default-blue hover:bg-default-dark-blue h-[65px] w-[65px] rounded-full shadow-md"
                onClick={async () => {
                  if (scheduleFormData?.document?.url) {
                    const shared = await shareFile({
                      url: scheduleFormData?.document.url,
                      filename: 'schedule-record.pdf',
                    });
                    if (!shared) {
                      window.open(scheduleFormData?.document.url, '_blank');
                    }
                  } else {
                    handlePrint();
                  }
                }}>
                <ShareIcon size={30} className="text-white" />
              </Button>
            </div>
          )}
        </SheetContent>
      </SheetPortal>
      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={formInstance.handleSubmit(
          (values) =>
            saveScheduleFormEditAsync(values)
              .then(() => {
                formInstance.reset(values);
                settleConfirm(true);
              })
              .catch(() => settleConfirm(false)),
          handleInvalidSave,
        )}
        discardCallback={() => {
          formInstance.reset();
          settleConfirm(true);
        }}
        loading={isSaving}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </Sheet>
  );
}
